import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
	SypacBox,
	SypacCheckbox,
	SypacText,
} from '@sypac/component-library-react';
import { NumericFormat } from 'react-number-format';
import {
	ClientOrderSummaryProps,
	ProductDetailProps,
} from './ClientOrderSummary.interface';
import { OrderContext } from '../../context/OrderContext/order.context';
import ModalSelfPickup from './components/ModalSelfPickup';
import {
	CompanyResponse,
	CompanyService,
} from '../../services/company.services';
import { toastVariant } from '../ToastVariant/toastVariant';
import { T, useTranslate } from '@tolgee/react';
import DashedDivider from '../DashedDivider/DashedDivider';
import { CUSTOMER_STORE_FLOW } from '../../types/order-flow';
import { numberTwoDecimal } from '../../utils/number.util';
import BankTransfer from '../../assets/BankTransfer';
import { AuthContext } from '../../context/context';

const Skeleton = ({ className }: { className?: string }) => (
	<div className={`animate-pulse bg-gray-10/50 rounded ${className}`} />
);

const ClientOrderSummary: React.FC<ClientOrderSummaryProps> = ({
	currentProduct,
	prices,
	quantity,
	step,
	loading = false,
}) => {
	const { t } = useTranslate();
	const language = localStorage.getItem('lang') || 'en';
	const { user } = useContext(AuthContext);
	const isPl = user?.profile?.countryCode === 'PL';
	const { order, setOrder, additional } = useContext(OrderContext);

	const isQuantityStep =
		step === 1 ||
		(additional?.orderFlow === CUSTOMER_STORE_FLOW.CONTRACT_LOCATION_FIRST &&
			step === 2);

	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [dontShowAnymore, setDontShowAnymore] = useState<boolean>(false);
	const [isDeliveryIncluded, setIsDeliveryIncluded] = useState<boolean>(
		order?.deliveryIncluded ?? true,
	);
	const [myCompany, setMyCompany] = useState<CompanyResponse>();

	const hasProductPrice =
		prices.productPriceForClient > 0 && prices.productPriceForClientWithVat > 0;

	const getMyCompanyCallback = useCallback(async () => {
		try {
			const { data } = await CompanyService.getMeCompany();
			setMyCompany(data);
		} catch (error) {
			return toastVariant(`Something went wrong. ${error?.toString()!}`, true);
		}
	}, []);

	const ProductDetail = ({
		label,
		value,
		isNumeric = false,
		isLoading = false,
	}: ProductDetailProps & { isLoading?: boolean }) => (
		<section className="flex justify-between">
			<SypacText variant="body-normal-medium">
				<p className="text-gray-40 text-[14px] leading-[16px] whitespace-nowrap">
					{label}
				</p>
			</SypacText>
			<DashedDivider
				dashLength={2}
				gapLength={4}
				color="#ABABAB"
				className="self-end mx-[10px]"
			/>
			<SypacText variant="body-normal-medium">
				<div className="text-gray-80 text-[14px] leading-[16px] whitespace-nowrap min-w-[60px] flex justify-end">
					{isLoading ? (
						<Skeleton className="h-4 w-[92px]" />
					) : isNumeric ? (
						<FormattedNumber value={value as string} />
					) : (
						value
					)}
				</div>
			</SypacText>
		</section>
	);

	const FormattedNumber = ({ value }: { value: string }) => (
		<NumericFormat
			type="text"
			displayType="text"
			thousandSeparator=" "
			decimalSeparator="."
			value={parseFloat(value ?? 0).toFixed(2)}
			suffix={currentProduct?.currency === 'lei' ? ' MDL' : ' PLN'}
		/>
	);

	const handleCheck = () => {
		setOrder({
			...order,
			deliveryIncluded: !isDeliveryIncluded,
		});
		setIsDeliveryIncluded(!isDeliveryIncluded);
		setIsModalOpen(false);
	};

	const handleDontShowAnymore = () => {
		setDontShowAnymore(true);
		handleCheck();
	};

	const openModal = () => {
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
	};

	useEffect(() => {
		getMyCompanyCallback().then(() => {});
	}, [getMyCompanyCallback]);

	return (
		<>
			<SypacBox className="w-full md:flex-row lg:flex-col gap-4 py-5 bg-white sm:shadow-drop-shadow">
				<div className="min-w-[240px] flex flex-col gap-4 px-3.5 sm:px-4">
					<SypacText variant="body-normal-medium">
						<p className="text-base sm:text-sm xl-2xl:text-base">
							<T keyName="clientOrderSummary.orderSummary">Order summary</T>
						</p>
					</SypacText>
					<ProductDetail
						label={t('clientOrderSummary.productName', 'Product name:')}
						value={
							currentProduct?.translations?.find(
								(locale) => locale.language === language,
							)?.name || currentProduct?.name
						}
					/>
					{currentProduct?.type ? (
						<ProductDetail
							label={t('clientOrderSummary.productType', 'Product type:')}
							value={`${
								currentProduct?.type ? `${currentProduct?.type}` : ''
							} ${currentProduct?.size ? `(${currentProduct?.size})` : ''}`}
						/>
					) : null}
					{!currentProduct?.type && currentProduct?.size ? (
						<ProductDetail
							label={t('clientOrderSummary.productSize', 'Product size:')}
							value={`(${currentProduct?.size})`}
						/>
					) : null}

					{!isPl && (
						<ProductDetail
							label={t(
								'clientOrderSummary.pricePerTonExcl',
								'Price per ton (excl. VAT):',
							)}
							value={
								hasProductPrice ? currentProduct?.pricePerUnitWithProcent! : 0
							}
							isNumeric
							isLoading={loading}
						/>
					)}

					<ProductDetail
						label={t('clientOrderSummary.quantity', 'Quantity:')}
						value={
							<T
								keyName="clientOrderSummary.quantityUnit"
								params={{
									q: `${quantity || 0}`,
								}}
							>
								ton
							</T>
						}
					/>

					{!isPl && (
						<ProductDetail
							label={
								<T keyName="clientOrderSummary.productVatExcl">
									Product (excl. VAT)
								</T>
							}
							value={hasProductPrice ? prices.productPriceForClient : 0}
							isNumeric
							isLoading={loading}
						/>
					)}
				</div>

				{isDeliveryIncluded && !isPl ? (
					<>
						<div className="border border-solid border-gray-10 border-t-0 border-l-0 border-r-0 mx-3 sm:mx-0" />
						<div className="min-w-[240px] flex flex-col gap-2 xl-2xl:gap-4 px-3.5 sm:px-4">
							<SypacText variant="body-normal-medium">
								<p className="text-base sm:text-sm xl-2xl:text-base">
									<T keyName="clientOrderSummary.deliveryCosts">
										Delivery costs
									</T>
								</p>
							</SypacText>
							<ProductDetail
								label={t(
									'clientOrderSummary.transportationVatExcl',
									'Transportation (excl. VAT):',
								)}
								value={hasProductPrice ? prices.transportPriceForClient : 0}
								isNumeric
								isLoading={loading}
							/>
						</div>
					</>
				) : null}

				<div className="border border-solid border-gray-10 border-t-0 border-l-0 border-r-0 mx-3 sm:mx-0" />

				<div className="md:ml-8 lg:ml-0 min-w-[240px] flex flex-col gap-4 px-3.5 sm:px-4">
					<SypacText variant="body-normal-medium">
						<p className="text-base sm:text-sm xl-2xl:text-base">
							<T keyName="clientOrderSummary.total">Total</T>
						</p>
					</SypacText>

					<div className="flex justify-between">
						<SypacText
							variant="body-normal-medium"
							className="mb-0 md:mb-3 lg:mb-0"
						>
							<p className="text-gray-40 text-base sm:text-sm xl-2xl:text-base whitespace-nowrap">
								<T keyName="clientOrderSummary.totalPriceExcl">
									Total price (excl. VAT)
								</T>
							</p>
						</SypacText>
						<DashedDivider
							dashLength={2}
							gapLength={4}
							color="#ABABAB"
							className="self-end mx-[10px]"
						/>
						<SypacText variant="body-normal-medium">
							<div className="text-base sm:text-sm xl-2xl:text-base whitespace-nowrap min-w-[60px] flex justify-end">
								{loading ? (
									<Skeleton className="h-5 w-[92px]" />
								) : (
									<FormattedNumber
										value={String(
											hasProductPrice
												? prices.productPriceForClient +
														(isDeliveryIncluded
															? prices.transportPriceForClient
															: 0)
												: 0,
										)}
									/>
								)}
							</div>
						</SypacText>
					</div>
				</div>

				{order?.contractData && !isPl ? (
					<>
						<div className="border border-solid border-gray-10 border-t-0 border-l-0 border-r-0 mx-3 sm:mx-0" />
						<div className="flex flex-col gap-4 px-3.5 sm:px-4">
							<div className="flex items-center gap-4">
								<div className="w-full flex flex-col gap-[6px] items-start justify-center p-3 bg-[#FAFAFA] rounded-10">
									<SypacText variant="body-normal-medium">
										<p className="text-[12px] leading-[14px] text-gray-60">
											<T keyName="clientSelectQuantity.priceInStore">
												Price in store
											</T>
										</p>
									</SypacText>
									<SypacText variant="body-normal-medium">
										<p className="text-[16px] leading-[24px] text-[#454545]">
											<NumericFormat
												type="text"
												displayType="text"
												thousandSeparator=" "
												decimalSeparator="."
												value={
													order?.contractData.lines?.[0].product.pricePerUnit ||
													0
												}
												suffix={` ${
													order?.contractData.countryCode === 'MD'
														? 'MDL'
														: 'PLN'
												} / ${t('producersProductCard.perTon', ' per ton')}`}
											/>
										</p>
									</SypacText>
								</div>
								<div className="w-full flex flex-col gap-[6px] items-start justify-center p-3 bg-[#FAFAFA] rounded-10">
									<SypacText variant="body-normal-medium">
										<p className="text-[12px] leading-[14px] text-gray-60">
											<T keyName="clientSelectQuantity.priceInContract">
												Price in contract
											</T>
										</p>
									</SypacText>
									<SypacText variant="body-normal-medium">
										<p className="text-[16px] leading-[24px] text-[#454545]">
											<NumericFormat
												type="text"
												displayType="text"
												thousandSeparator=" "
												decimalSeparator="."
												value={
													numberTwoDecimal(
														order?.contractData.lines?.[0].pricePerTonBuyer *
															(1 + order?.contractData.lines?.[0].markup / 100),
													) || 0
												}
												suffix={` ${
													order?.contractData.countryCode === 'MD'
														? 'MDL'
														: 'PLN'
												} / ${t('producersProductCard.perTon', ' per ton')}`}
											/>
										</p>
									</SypacText>
								</div>
							</div>

							{order?.contractData.lines[0].ignoreCreditLimit && (
								<div className="flex items-center gap-2">
									<BankTransfer color="#454545" />

									<div className="flex flex-col gap-[2px]">
										<SypacText variant="body-regular-medium">
											<p className="text-[14px] leading-[16px] text-gray-90">
												<T keyName="generalTab.creditLimitDisabled">
													Credit limit disabled
												</T>
											</p>
										</SypacText>
										<SypacText variant="body-regular-medium">
											<p className="text-[14px] leading-[16px] text-gray-50">
												<T keyName="generalTab.creditLimitTooltipBuyer">
													Orders you place from this contract
												</T>
											</p>
										</SypacText>
									</div>
								</div>
							)}
						</div>
					</>
				) : null}

				{isQuantityStep && myCompany?.alowExcludedTransporter ? (
					<>
						<div className="border border-solid border-gray-10 border-t-0 border-l-0 border-r-0 mx-3 sm:mx-0" />
						<div className="md:ml-8 lg:ml-0 min-w-[240px] px-3.5 sm:px-4">
							<div className="h-[22px] col-start-2 flex items-center gap-4">
								<SypacCheckbox size="md">
									<input
										type="checkbox"
										name="unlimited"
										checked={isDeliveryIncluded}
										onChange={
											dontShowAnymore
												? handleCheck
												: isDeliveryIncluded
												? openModal
												: handleCheck
										}
										className="cursor-pointer"
									/>
								</SypacCheckbox>
								<SypacText variant="overline-normal-large">
									<p className="text-base sm:text-sm xl-2xl:text-base mt-[2px]">
										{isDeliveryIncluded ? (
											<T keyName="clientOrderSummary.deliveryIncluded">
												Delivery included
											</T>
										) : (
											<T keyName="clientOrderSummary.deliveryExcluded">
												Delivery excluded
											</T>
										)}
									</p>
								</SypacText>
							</div>
						</div>
					</>
				) : null}
			</SypacBox>
			<ModalSelfPickup
				isOpen={isModalOpen}
				onClose={closeModal}
				onSave={handleCheck}
				dontShowAnymore={handleDontShowAnymore}
				isChecked={dontShowAnymore}
			/>
		</>
	);
};

export default ClientOrderSummary;
